/**
 * Steuerberater-Auswertung „Mahngebühren"
 * Route: /finanzen/steuer/mahngebuehren
 *
 * Zeigt pro Jahr (oder freier Zeitraum) alle Mahngebühren-Buchungen mit
 * Bezahl-Status. Optional Filter nach Mieter/Objekt. Exporte als CSV & PDF.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, DownloadSimple, FilePdf, ChartLineUp, CurrencyEur } from "@phosphor-icons/react";
import { formatEUR, formatDate } from "@/lib/constants";

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - i);

export default function MahngebuehrenReport() {
  const [year, setYear] = useState(currentYear);
  const [customRange, setCustomRange] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [tenancyId, setTenancyId] = useState("");
  const [properties, setProperties] = useState([]);
  const [tenancies, setTenancies] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [ps, ts] = await Promise.all([
          api.get("/properties?limit=500"),
          api.get("/tenancies?status=active&limit=500"),
        ]);
        setProperties(ps.data.items || ps.data || []);
        setTenancies(ts.data.items || ts.data || []);
      } catch (e) {
        // silent
      }
    })();
  }, []);

  const params = useMemo(() => {
    const p = {};
    if (customRange && dateFrom && dateTo) {
      p.date_from = dateFrom;
      p.date_to = dateTo;
    } else {
      p.year = year;
    }
    if (propertyId) p.property_id = propertyId;
    if (tenancyId) p.tenancy_id = tenancyId;
    return p;
  }, [customRange, dateFrom, dateTo, year, propertyId, tenancyId]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dunning/report/mahngebuehren", { params });
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report konnte nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [year, customRange, dateFrom, dateTo, propertyId, tenancyId]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportFile = async (format) => {
    try {
      const q = new URLSearchParams(params).toString();
      const url = `/dunning/report/mahngebuehren.${format}?${q}`;
      const { data: blob } = await api.get(url, { responseType: "blob" });
      const filename = `mahngebuehren_${params.year || `${params.date_from}_${params.date_to}`}.${format}`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error("Export fehlgeschlagen");
    }
  };

  return (
    <div className="space-y-4" data-testid="mahngebuehren-report-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <CurrencyEur size={24} weight="bold" /> Mahngebühren-Auswertung
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Für den Steuerberater: alle in Rechnung gestellten Mahngebühren mit Zahlstatus, exportierbar als CSV oder PDF.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => exportFile("csv")} className="gap-1.5" data-testid="export-csv">
            <DownloadSimple size={14} /> CSV
          </Button>
          <Button variant="outline" onClick={() => exportFile("pdf")} className="gap-1.5" data-testid="export-pdf">
            <FilePdf size={14} /> PDF
          </Button>
        </div>
      </div>

      {/* --- Filter --- */}
      <div className="bg-white border border-slate-200 rounded-md p-4 grid md:grid-cols-4 gap-3">
        <div>
          <Label className="text-xs">Zeitraum</Label>
          <Select value={customRange ? "custom" : String(year)} onValueChange={(v) => {
            if (v === "custom") setCustomRange(true);
            else { setCustomRange(false); setYear(Number(v)); }
          }}>
            <SelectTrigger data-testid="year-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {YEAR_OPTIONS.map((y) => <SelectItem key={y} value={String(y)}>Kalenderjahr {y}</SelectItem>)}
              <SelectItem value="custom">Freier Zeitraum …</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {customRange && (
          <>
            <div>
              <Label className="text-xs">Von</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="date-from" />
            </div>
            <div>
              <Label className="text-xs">Bis</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="date-to" />
            </div>
          </>
        )}
        <div>
          <Label className="text-xs">Objekt (optional)</Label>
          <Select value={propertyId || "__all__"} onValueChange={(v) => setPropertyId(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="property-filter"><SelectValue placeholder="Alle Objekte" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle Objekte</SelectItem>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.property_number} · {p.name || p.address?.street}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Mietverhältnis (optional)</Label>
          <Select value={tenancyId || "__all__"} onValueChange={(v) => setTenancyId(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="tenancy-filter"><SelectValue placeholder="Alle MV" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle Mietverhältnisse</SelectItem>
              {tenancies.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.contract_number}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* --- KPIs --- */}
      {data && (
        <div className="grid md:grid-cols-3 gap-3">
          <KpiCard label="Bezahlte Mahngebühren" value={formatEUR(data.total_paid_eur)} sub={`${data.count_paid} Positionen`} color="emerald" testid="kpi-paid" />
          <KpiCard label="Offene Mahngebühren" value={formatEUR(data.total_open_eur)} sub={`${data.count_open} Positionen`} color="rose" testid="kpi-open" />
          <KpiCard label="Gesamt" value={formatEUR((data.total_paid_eur || 0) + (data.total_open_eur || 0))} sub={`${data.rows.length} Positionen · ${data.period.from} bis ${data.period.to}`} color="slate" testid="kpi-total" />
        </div>
      )}

      {/* --- Tabelle --- */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Datum</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Vertrag</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Mieter</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Objekt</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600 text-right">Betrag</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Status</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Bezahlt am</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase text-slate-600">Bezeichnung</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-slate-500 text-sm">Lade …</td></tr>}
            {!loading && (!data || data.rows.length === 0) && (
              <tr><td colSpan={8} className="p-8 text-center text-slate-400 text-sm">
                <FileText size={28} className="mx-auto mb-2 text-slate-300" />
                Keine Mahngebühren im gewählten Zeitraum.
              </td></tr>
            )}
            {!loading && data?.rows?.map((r) => (
              <tr key={r.posting_id} className="border-t border-slate-100" data-testid={`report-row-${r.posting_id}`}>
                <td className="px-3 py-2 whitespace-nowrap">{formatDate(r.posting_date)}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.contract_number || "—"}</td>
                <td className="px-3 py-2">{r.tenant_name || "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-600">{r.property_label || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium">{formatEUR(r.amount_eur)}</td>
                <td className="px-3 py-2">
                  {r.paid ? (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">bezahlt</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">offen</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs">{r.pay_date ? formatDate(r.pay_date) : "—"}</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.reference || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KpiCard({ label, value, sub, color, testid }) {
  const colors = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  return (
    <div className={`border rounded-md p-4 ${colors[color] || colors.slate}`} data-testid={testid}>
      <div className="text-xs uppercase tracking-wider opacity-70 flex items-center gap-1.5"><ChartLineUp size={12} /> {label}</div>
      <div className="text-2xl font-semibold mt-1 tabular-nums">{value}</div>
      <div className="text-xs opacity-70 mt-0.5">{sub}</div>
    </div>
  );
}
